/**
 * The install packet — JOB-SOLD-01 on the phone (Seth 2026-09-28: "He puts together a proposal, takes pictures
 * of the exterior and interior of the home for the measurements to be done by someone in the office for the load
 * calcs. They fill out the installer guide with notes and everything the job entails").
 *
 * Everything the office needs to start the install, caught while the tech is still in the house:
 *   · Photos for the Load Calc — a checklist (Settings → Sold Jobs & Load Calc), one tap per photo, plus more
 *     photos and a Sketch (grid paper: the outline of the house, where the unit goes)
 *   · Site Facts — square feet, basement, stories, windows, insulation, the old unit's size … tap, don't type
 *   · the Installer Guide — the same questions as the office's Work Order, tailored to what was sold
 *   · utility accounts for the rebates, the day they'd like, notes for the office and crew
 * It saves as the tech goes (a dropped signal or a closed app loses nothing) onto the job — or onto the proposal
 * while they're still deciding, carried into the job the moment they say yes. Send to the Office stamps it and
 * lists anything missing; it never blocks.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, CalendarDays, Camera, Check, ImagePlus, Loader2, PenLine, Send } from "lucide-react";
import { cn } from "@/lib/cn";
import { supabase } from "@/lib/supabase";
import { haptic } from "@/native/boot";
import { InfoTip } from "@/components/InfoTip";
import {
  isImageFile, uploadOneFile, useDispatchFiles, useJobFiles, useSignedUrls, invalidateFileQueries, type PegasusFile,
} from "@/features/files/filesCore";
import { MarkupEditor } from "@/features/files/MarkupEditor";
import { InstallDetailsCard } from "@/features/jobs/InstallDetailsCard";
import { specProgress, useInstallBank, useJobEquipFacts } from "@/features/jobs/installSpec";
import { useCreateJobFromQuote, useJob, useJobEquipment } from "@/features/jobs/jobs";
import {
  savePacket, siteCaption, useSalePhotoSlots, useSiteFacts, type SalePacket, type SiteFact,
} from "@/features/jobs/saleHandoff";

export interface PacketStop {
  ref: string;
  no: string;
  name: string;
  custno: string | null;
  locno: string | null;
}

const card = "rounded-3xl border border-slate-200 bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]";
const input = "mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-[16px] text-slate-900 placeholder:text-slate-400 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/25";
const money$ = (n: number) => `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const todayISO = () => new Date().toLocaleDateString("en-CA");

/** Booked installs per day, next ~3 weeks — so the tech can talk real dates at the kitchen table. */
function useInstallOutlook() {
  return useQuery({
    queryKey: ["m_install_outlook"],
    staleTime: 60 * 1000,
    queryFn: async (): Promise<Map<string, number>> => {
      const start = todayISO();
      const end = new Date(Date.now() + 21 * 86400000).toISOString().slice(0, 10);
      const { data } = await supabase
        .from("pegasus_dispatch")
        .select("scheduled_date")
        .eq("lane_key", "install")
        .not("scheduled_date", "is", null)
        .gte("scheduled_date", start)
        .lte("scheduled_date", end)
        .is("deleted_at", null);
      const m = new Map<string, number>();
      for (const r of (data ?? []) as { scheduled_date: string }[]) m.set(r.scheduled_date, (m.get(r.scheduled_date) ?? 0) + 1);
      return m;
    },
  });
}

interface AcceptedLine { id: string; kind: string; item_id: string | null; description: string | null; model: string | null; manufacturer: string | null; qty: number; unit_price: number; discount: number }

export function InstallPacket({ s, techFirst, empno, quoteId, onClose }: {
  s: PacketStop;
  techFirst: string | null;
  empno?: string | null;
  quoteId: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const createJob = useCreateJobFromQuote();
  const outlook = useInstallOutlook().data;
  const [jobId, setJobId] = useState<string | null>(null);
  const [phase, setPhase] = useState<"job" | "form" | "done">("job");
  const [err, setErr] = useState<string | null>(null);

  // The proposal: accepted option (the job's contents), its job if acceptance already made it, the prep packet.
  const accepted = useQuery({
    queryKey: ["m_accepted_install", quoteId],
    queryFn: async () => {
      const { data: quote } = await supabase
        .from("pegasus_quote")
        .select("id, title, accepted_option_id, job_id, custno, locno, dispatch_no, notes, prep_packet")
        .eq("id", quoteId)
        .single();
      const qrow = quote as { id: string; title: string | null; accepted_option_id: string | null; job_id: string | null; custno: string | null; locno: string | null; dispatch_no: string | null; notes: string | null; prep_packet: SalePacket | null };
      if (!qrow.accepted_option_id) return { qrow, opt: null, lines: [] as AcceptedLine[] };
      const [{ data: opt }, { data: lines }] = await Promise.all([
        supabase.from("pegasus_quote_option").select("id, label, tier, rebate_total, discount_total").eq("id", qrow.accepted_option_id).single(),
        supabase.from("pegasus_quote_line").select("id, kind, item_id, description, model, manufacturer, qty, unit_price, discount").eq("option_id", qrow.accepted_option_id).order("sort"),
      ]);
      return { qrow, opt: opt as { id: string; label: string | null; tier: string; rebate_total: number; discount_total: number } | null, lines: (lines ?? []) as AcceptedLine[] };
    },
  });

  // Sold → the job (accepting already made it; the button is the fallback). Undecided → straight to the packet,
  // saved on the proposal.
  const sold = !!accepted.data?.qrow.accepted_option_id;
  useEffect(() => {
    if (!accepted.data) return;
    if (accepted.data.qrow.job_id && !jobId) {
      setJobId(accepted.data.qrow.job_id);
      setPhase((p) => (p === "job" ? "form" : p));
    } else if (!accepted.data.qrow.accepted_option_id) {
      setPhase((p) => (p === "job" ? "form" : p));
    }
  }, [accepted.data, jobId]);

  const sellPrice = (accepted.data?.lines ?? []).reduce((t, l) => t + Math.max(0, l.qty * (Number(l.unit_price) || 0) - (Number(l.discount) || 0)), 0)
    - (Number(accepted.data?.opt?.discount_total) || 0);

  const makeJob = () => {
    createJob.mutate({ quoteId, saleTech: empno ?? null }, {
      onSuccess: (job) => { setJobId(job.id); setPhase("form"); qc.invalidateQueries({ queryKey: ["m_accepted_install", quoteId] }); },
      onError: () => setErr("Couldn't create the job — check signal and tap again."),
    });
  };

  return (
    <div className="fixed inset-0 z-50 mx-auto flex max-w-md flex-col bg-[#f6f5f1]">
      <header className="flex items-center gap-2 border-b border-slate-200 bg-white px-3 py-2.5">
        <button type="button" onClick={onClose} className="flex h-11 w-11 items-center justify-center rounded-xl text-slate-500 active:bg-slate-100" aria-label="Back to the call">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="min-w-0 flex-1">
          <div className="text-[17px] font-bold text-slate-900">Install Packet</div>
          <div className="truncate text-[12.5px] text-slate-500">{s.name} · {sold ? "Sold ✓" : "Prepping — not sold yet"}</div>
        </div>
      </header>

      {phase === "done" ? (
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100"><Check className="h-8 w-8 text-emerald-600" /></span>
          <h3 className="text-xl font-extrabold text-slate-900">Sent to the Office ✓</h3>
          <p className="max-w-[32ch] text-[15px] leading-relaxed text-slate-600">
            {jobId
              ? "The office has the photos and facts for the load calc. Dispatch will confirm the install date with the customer."
              : "Saved with the proposal — if they say yes later, it goes straight onto the job."}
          </p>
          <button type="button" onClick={() => setPhase("form")} className="mt-1 text-[15px] font-semibold text-slate-600 underline underline-offset-2">Keep Adding</button>
          <button type="button" onClick={onClose} className="mt-2 w-full rounded-2xl bg-emerald-600 py-4 text-[16px] font-bold text-white active:opacity-90">
            Back to the Call
          </button>
        </div>
      ) : phase === "job" ? (
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
          <section className="rounded-3xl border border-emerald-200 bg-emerald-50/60 p-4">
            <h4 className="text-[15px] font-bold tracking-tight text-emerald-700">They accepted</h4>
            <div className="mt-1 text-[16px] font-extrabold text-slate-900">{accepted.data?.opt?.label ?? "Selected option"} · {money$(Math.max(0, sellPrice))}</div>
            <ul className="mt-2 space-y-1">
              {(accepted.data?.lines ?? []).map((l) => (
                <li key={l.id} className="text-[14px] text-slate-600">{l.qty > 1 ? `${l.qty}× ` : ""}{l.description ?? l.model}</li>
              ))}
            </ul>
          </section>
          {err && <p className="text-center text-[14px] font-semibold text-red-600">{err}</p>}
          <button type="button" disabled={createJob.isPending || accepted.isLoading} onClick={makeJob}
            className="w-full rounded-2xl bg-slate-900 py-4 text-[16px] font-bold text-white active:opacity-90 disabled:opacity-60">
            {createJob.isPending ? "Opening the job…" : "Open the Install Job →"}
          </button>
        </div>
      ) : (
        <PacketForm s={s} techFirst={techFirst} quoteId={quoteId} jobId={jobId}
          prep={(accepted.data?.qrow.prep_packet ?? null) as SalePacket | null}
          quoteNotes={accepted.data?.qrow.notes ?? null}
          outlook={outlook}
          loaded={!accepted.isLoading}
          onSent={() => { haptic("success"); setPhase("done"); }} />
      )}
    </div>
  );
}

/* ── The form ─────────────────────────────────────────────────────────────── */

function PacketForm({ s, techFirst, quoteId, jobId, prep, quoteNotes, outlook, loaded, onSent }: {
  s: PacketStop; techFirst: string | null; quoteId: string; jobId: string | null; prep: SalePacket | null; quoteNotes: string | null;
  outlook: Map<string, number> | undefined; loaded: boolean; onSent: () => void;
}) {
  const qc = useQueryClient();
  const { data: job } = useJob(jobId);
  const { data: slotsQ } = useSalePhotoSlots();
  const { data: factsQ } = useSiteFacts();
  const slots = (slotsQ ?? []).filter((x) => x?.label?.trim());
  const factList = (factsQ ?? []).filter((f) => f?.label?.trim());
  const { data: jobFiles } = useJobFiles(jobId ?? undefined);
  const { data: callFiles } = useDispatchFiles(s.ref);
  const files = useMemo(() => {
    const m = new Map<string, PegasusFile>();
    for (const f of [...(jobFiles ?? []), ...(callFiles ?? [])]) if (!f.archived) m.set(f.id, f);
    return [...m.values()];
  }, [jobFiles, callFiles]);

  // The packet on screen; filled once from what's saved (the job's, else the proposal's), then saved as it changes.
  const [facts, setFacts] = useState<Record<string, string>>({});
  const [util, setUtil] = useState<Record<string, string>>({});
  const [notes, setNotes] = useState("");
  const [prefDay, setPrefDay] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busySlot, setBusySlot] = useState<string | null>(null);
  const [sketching, setSketching] = useState(false);
  const pickRef = useRef<HTMLInputElement>(null);
  const target = useRef<string | null>(null);
  const timer = useRef<number | null>(null);
  const dirty = useRef(false);

  const saved: SalePacket | null = jobId ? ((job?.sale_packet ?? null) as SalePacket | null) : prep;
  useEffect(() => {
    if (ready || !loaded || (jobId && !job)) return;
    const p = saved ?? {};
    setFacts(p.facts ?? {});
    setUtil({ ...(p.utility ?? {}) } as Record<string, string>);
    setNotes(p.notes ?? "");
    setPrefDay(p.pref_day ?? null);
    setReady(true);
  }, [ready, loaded, jobId, job, saved]);

  const where = jobId ? { jobId } : { quoteId };
  const flush = async () => {
    if (timer.current) { window.clearTimeout(timer.current); timer.current = null; }
    if (!dirty.current) return;
    dirty.current = false;
    setSaveState("saving");
    try {
      await savePacket(where, { facts, notes, utility: util, pref_day: prefDay, tech: techFirst, from_call: s.no });
      setSaveState("saved");
      qc.invalidateQueries({ queryKey: ["job", jobId] });
    } catch {
      dirty.current = true;
      setSaveState("error");
    }
  };
  const flushRef = useRef(flush);
  flushRef.current = flush;
  // Save a moment after the last tap or keystroke; leaving the screen saves too.
  useEffect(() => {
    if (!ready) return;
    dirty.current = true;
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => void flushRef.current(), 900);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facts, util, notes, prefDay]);
  useEffect(() => () => { void flushRef.current(); }, []);
  // The first fill isn't a change.
  useEffect(() => { if (ready) dirty.current = false; }, [ready]);

  const slotFiles = (label: string) => files.filter((f) => f.caption === siteCaption(label));
  const others = files.filter((f) => f.kind === "photo" && !/^Site — /.test(f.caption ?? "") && !/^(before|after)\b/i.test(f.caption ?? ""));
  const shown = [...slots.flatMap((x) => slotFiles(x.label.trim()).slice(0, 1)), ...others.slice(0, 8)];
  const urls = useSignedUrls(shown.filter((f) => isImageFile(f))).data;
  const photoTarget = { custno: s.custno ?? "", locno: s.locno, dispatch: s.ref, job_id: jobId, origin: "tech" as const };

  const pick = (label: string | null) => { target.current = label; pickRef.current?.click(); };
  const onPhotos = async (list: FileList | null) => {
    const label = target.current;
    if (!list?.length || !s.custno) return;
    setBusySlot(label ?? "__more");
    setErr(null);
    try {
      for (const f of Array.from(list)) {
        await uploadOneFile({ ...photoTarget, doc_tag: label ? "photo" : null, sort: !label, caption: label ? siteCaption(label) : `Install site — call #${s.no}` }, f);
      }
      haptic("success");
      invalidateFileQueries(qc);
    } catch {
      setErr("A photo didn't upload — check the signal and try again.");
    } finally {
      setBusySlot(null);
      if (pickRef.current) pickRef.current.value = "";
    }
  };

  const reqSlots = slots.filter((x) => x.required);
  const photosMissing = reqSlots.filter((x) => slotFiles(x.label.trim()).length === 0);
  const factsMissing = factList.filter((f) => f.required && !(facts[f.key] ?? "").trim());

  const send = async () => {
    setSending(true);
    setErr(null);
    try {
      const { data: u } = await supabase.auth.getUser();
      dirty.current = false;
      if (timer.current) { window.clearTimeout(timer.current); timer.current = null; }
      const now = new Date().toISOString();
      await savePacket(where, { facts, notes, utility: util, pref_day: prefDay, tech: techFirst, from_call: s.no, sent_at: now, sent_by: u?.user?.email ?? null });
      // The board and the crew still read the install visit's notes — one short summary, replaced (not piled up) on a re-send.
      const summary = [
        `${jobId ? "INSTALL PACKET" : "INSTALL PREP (not sold yet)"} — from call #${s.no}${techFirst ? ` by ${techFirst}` : ""}`,
        ...factList.filter((f) => (facts[f.key] ?? "").trim()).map((f) => `${f.label}: ${facts[f.key].trim()}${f.unit && /^\d[\d,.]*$/.test(facts[f.key].trim()) ? ` ${f.unit}` : ""}`),
        notes.trim() ? `Notes: ${notes.trim()}` : null,
        prefDay ? `Would like the install ${new Date(prefDay + "T00:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })} — office confirms.` : null,
        "Photos and the full packet are on the job's Sold tab.",
      ].filter(Boolean).join("\n");
      const replaceBlock = (was: string | null) =>
        [...(was ?? "").split(/\n{2,}/).filter((b) => b.trim() && !/^INSTALL (PACKET|PREP)/.test(b.trim())), summary].join("\n\n");
      if (jobId) {
        const { data: visit } = await supabase.from("pegasus_dispatch").select("id, notes").eq("job_id", jobId).eq("lane_key", "install").is("deleted_at", null).limit(1).maybeSingle();
        const v = visit as { id: string; notes: string | null } | null;
        if (v) await supabase.from("pegasus_dispatch").update({ notes: replaceBlock(v.notes), updated_at: now }).eq("id", v.id);
        qc.invalidateQueries({ queryKey: ["job", jobId] });
      } else {
        await supabase.from("pegasus_quote").update({ notes: replaceBlock(quoteNotes), updated_at: now }).eq("id", quoteId);
        qc.invalidateQueries({ queryKey: ["m_accepted_install", quoteId] });
      }
      qc.invalidateQueries({ queryKey: ["pending_tickets"] });
      qc.invalidateQueries({ queryKey: ["jobs"] });
      onSent();
    } catch {
      setErr("Couldn't send it — check the signal and try again. Nothing you entered is lost.");
    } finally {
      setSending(false);
    }
  };

  // Next 15 weekdays for the preferred-day strip.
  const days: string[] = [];
  for (let d = 1; days.length < 15; d++) {
    const dt = new Date(Date.now() + d * 86400000);
    if (dt.getDay() === 0 || dt.getDay() === 6) continue;
    days.push(dt.toISOString().slice(0, 10));
  }

  return (
    <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4 pb-8">
      <div className="flex items-center justify-end text-[12.5px] font-medium text-slate-400">
        {saveState === "saving" ? <span className="inline-flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Saving…</span>
          : saveState === "saved" ? <span className="inline-flex items-center gap-1 text-emerald-700"><Check className="h-3 w-3" strokeWidth={3} /> Saved</span>
          : saveState === "error" ? <button type="button" onClick={() => void flush()} className="font-semibold text-red-600">Couldn&apos;t save — tap to retry</button>
          : "Saves as you go"}
      </div>

      {/* Photos for the load calc */}
      <section className={card}>
        <div className="flex items-center gap-2">
          <Camera className="h-4 w-4 text-slate-500" />
          <h4 className="text-[16px] font-bold tracking-tight text-slate-900">Photos for the Load Calc</h4>
          <InfoTip label="What these are for">The office measures the house and does the load calc from these. The four sides show the walls and windows; the data tags show what&apos;s there now.</InfoTip>
          {reqSlots.length > 0 && (
            <span className={cn("ml-auto rounded-full px-2.5 py-1 text-[12.5px] font-bold", photosMissing.length ? "bg-amber-50 text-amber-800" : "bg-emerald-50 text-emerald-700")}>
              {reqSlots.length - photosMissing.length} of {reqSlots.length}
            </span>
          )}
        </div>
        <input ref={pickRef} type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={(e) => void onPhotos(e.target.files)} />
        <ul className="mt-2 divide-y divide-slate-100">
          {slots.map((x) => {
            const label = x.label.trim();
            const got = slotFiles(label);
            const first = got[0];
            const url = first ? urls?.get(first.storage_path) : null;
            return (
              <li key={label} className="flex items-center gap-3 py-2">
                <button type="button" onClick={() => pick(label)} disabled={busySlot === label}
                  aria-label={first ? `Add another photo: ${label}` : `Take a photo: ${label}`}
                  className={cn("flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-2xl [touch-action:manipulation]",
                    first ? "bg-slate-200" : "border-2 border-dashed border-sky-300 bg-sky-50 text-sky-700")}>
                  {busySlot === label ? <Loader2 className="h-5 w-5 animate-spin text-slate-500" />
                    : url ? <img src={url} alt="" className="h-full w-full object-cover" />
                    : first ? <Check className="h-5 w-5 text-emerald-600" /> : <Camera className="h-5 w-5" />}
                </button>
                <button type="button" onClick={() => pick(label)} className="min-w-0 flex-1 text-left">
                  <span className="block text-[15px] font-semibold text-slate-800">{label}</span>
                  {got.length > 1 ? <span className="block text-[12.5px] text-slate-500">{got.length} photos</span>
                    : x.hint && !first ? <span className="block text-[12.5px] text-slate-500">{x.hint}</span> : null}
                </button>
                {first ? <Check className="h-5 w-5 shrink-0 text-emerald-600" strokeWidth={3} />
                  : x.required ? <span className="shrink-0 rounded-full bg-amber-50 px-2.5 py-1 text-[12px] font-bold text-amber-800">Needed</span>
                  : <span className="shrink-0 text-[12.5px] font-semibold text-slate-400">Optional</span>}
              </li>
            );
          })}
        </ul>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <button type="button" onClick={() => pick(null)} disabled={busySlot === "__more"}
            className="flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white py-3.5 text-[15px] font-bold text-slate-700 active:bg-slate-50 disabled:opacity-60">
            {busySlot === "__more" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />} More Photos
          </button>
          <button type="button" onClick={() => setSketching(true)} disabled={!s.custno}
            className="flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white py-3.5 text-[15px] font-bold text-slate-700 active:bg-slate-50 disabled:opacity-60">
            <PenLine className="h-4 w-4" /> Sketch
          </button>
        </div>
        {others.length > 0 && (
          <div className="mt-2 flex gap-2 overflow-x-auto pb-1">
            {others.slice(0, 8).map((f) => (
              <span key={f.id} className="h-14 w-14 shrink-0 overflow-hidden rounded-xl bg-slate-200">
                {urls?.get(f.storage_path) && <img src={urls.get(f.storage_path)} alt="" className="h-full w-full object-cover" />}
              </span>
            ))}
            {others.length > 8 && <span className="flex h-14 shrink-0 items-center px-2 text-[13px] font-semibold text-slate-500">+{others.length - 8}</span>}
          </div>
        )}
      </section>

      {/* Site facts */}
      <section className={cn(card, "space-y-3")}>
        <h4 className="text-[16px] font-bold tracking-tight text-slate-900">Site Facts</h4>
        {factList.map((f) => <FactInput key={f.key} f={f} value={facts[f.key] ?? ""} onChange={(v) => setFacts((m) => ({ ...m, [f.key]: v }))} />)}
      </section>

      {/* The installer guide — only once there's a job (the questions follow what was sold). */}
      {jobId && job && <PhoneGuide jobId={jobId} />}

      {/* Notes */}
      <section className={card}>
        <label className="text-[16px] font-bold tracking-tight text-slate-900" htmlFor="packet-notes">Notes for the Office &amp; Crew</label>
        <textarea id="packet-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
          placeholder="Dog in the yard · park in back · customer works from home"
          className={input} />
      </section>

      {/* Utility accounts — the office files the rebates. */}
      <section className="rounded-3xl border border-emerald-200 bg-emerald-50/40 p-4">
        <div className="flex items-center gap-2">
          <h4 className="text-[16px] font-bold tracking-tight text-emerald-800">Utility Accounts</h4>
          <InfoTip label="Why these">We file the rebates for the customer. A photo of a utility bill in More Photos works too.</InfoTip>
        </div>
        <div className="mt-2 grid grid-cols-2 gap-2">
          {([["eu", "Electric utility", "Xcel Energy"], ["ea", "Electric account #", "51-1234567-8"], ["gu", "Gas utility", "CenterPoint"], ["ga", "Gas account #", "8000123456"]] as Array<[string, string, string]>).map(([k, label, ph]) => (
            <label key={k} className="block">
              <span className="text-[13px] font-semibold text-slate-600">{label}</span>
              <input type="text" value={util[k] ?? ""} onChange={(e) => setUtil((m) => ({ ...m, [k]: e.target.value }))} placeholder={ph} className={input} />
            </label>
          ))}
        </div>
      </section>

      {/* Preferred day against the live install calendar */}
      <section className={card}>
        <div className="flex items-center gap-2">
          <CalendarDays className="h-4 w-4 text-slate-500" />
          <h4 className="text-[16px] font-bold tracking-tight text-slate-900">When Works for Them?</h4>
          <InfoTip label="About these days">The live install calendar — lighter days are easier to land. Dispatch confirms the final date.</InfoTip>
        </div>
        <div className="mt-2 grid grid-cols-5 gap-1.5">
          {days.map((d) => {
            const booked = outlook?.get(d) ?? 0;
            const dt = new Date(d + "T00:00:00");
            const sel = prefDay === d;
            return (
              <button key={d} type="button" onClick={() => setPrefDay(sel ? null : d)}
                className={cn("flex flex-col items-center rounded-xl border py-1.5 [touch-action:manipulation]",
                  sel ? "border-slate-900 bg-slate-900 text-white"
                  : booked === 0 ? "border-emerald-200 bg-emerald-50 text-slate-700"
                  : booked <= 2 ? "border-slate-200 bg-white text-slate-700"
                  : "border-amber-200 bg-amber-50 text-slate-600")}>
                <span className="text-[10px] font-bold uppercase">{dt.toLocaleDateString("en-US", { weekday: "short" })}</span>
                <span className="text-[14px] font-extrabold tabular-nums">{dt.getDate()}</span>
                <span className={cn("text-[9px] font-semibold", sel ? "text-white/70" : "text-slate-400")}>{booked === 0 ? "open" : `${booked} booked`}</span>
              </button>
            );
          })}
        </div>
      </section>

      {/* Before you send — what's still missing (it never blocks). */}
      {(photosMissing.length > 0 || factsMissing.length > 0) && (
        <section className="rounded-3xl border border-amber-200 bg-amber-50/70 p-4">
          <h4 className="text-[15px] font-bold text-amber-900">Still missing</h4>
          <p className="mt-1 text-[14px] leading-relaxed text-amber-900">
            {[...photosMissing.map((x) => x.label.trim()), ...factsMissing.map((f) => f.label)].join(" · ")}
          </p>
        </section>
      )}

      {err && <p className="text-center text-[14px] font-semibold text-red-600">{err}</p>}
      <button type="button" disabled={sending || !ready} onClick={() => void send()}
        className="flex w-full items-center justify-center gap-2 rounded-2xl bg-emerald-600 py-4 text-[16px] font-bold text-white active:opacity-90 disabled:opacity-60">
        {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
        {sending ? "Sending…" : photosMissing.length || factsMissing.length ? "Send to the Office Anyway" : "Send to the Office"}
      </button>

      {sketching && s.custno && (
        <MarkupEditor
          sketch={{ target: { ...photoTarget }, title: "House Sketch" }}
          origin="tech"
          onClose={() => setSketching(false)}
          onSaved={() => { setSketching(false); invalidateFileQueries(qc); haptic("success"); }}
        />
      )}
    </div>
  );
}

function FactInput({ f, value, onChange }: { f: SiteFact; value: string; onChange: (v: string) => void }) {
  const label = (
    <span className="flex items-center gap-1.5 text-[14px] font-semibold text-slate-700">
      {f.label}
      {f.required && !value.trim() && <span className="h-2 w-2 rounded-full bg-amber-400" aria-label="Needed" />}
    </span>
  );
  if (f.kind === "choice" && f.options?.length) {
    const extra = value && !f.options.includes(value) ? value : null;
    return (
      <div>
        {label}
        <div className="mt-1.5 flex flex-wrap gap-1.5" role="group" aria-label={f.label}>
          {[...f.options, ...(extra ? [extra] : [])].map((o) => (
            <button key={o} type="button" aria-pressed={value === o} onClick={() => onChange(value === o ? "" : o)}
              className={cn("h-11 rounded-full px-4 text-[15px] font-semibold [touch-action:manipulation]",
                value === o ? "bg-slate-900 text-white" : "bg-white text-slate-700 ring-1 ring-inset ring-slate-200 active:bg-slate-100")}>
              {o}
            </button>
          ))}
        </div>
      </div>
    );
  }
  return (
    <label className="block">
      {label}
      <span className="relative block">
        <input value={value} onChange={(e) => onChange(f.kind === "number" ? e.target.value.replace(/[^\d,.]/g, "") : e.target.value)}
          inputMode={f.kind === "number" ? "numeric" : undefined} placeholder={f.placeholder}
          className={cn(input, f.unit && "pr-16")} />
        {f.unit && <span className="pointer-events-none absolute right-3 top-1/2 translate-y-[-30%] text-[13px] text-slate-400">{f.unit}</span>}
      </span>
    </label>
  );
}

/** The installer guide, answered on the phone — the office's Work Order card, phone-sized. */
function PhoneGuide({ jobId }: { jobId: string }) {
  const { data: job } = useJob(jobId);
  const { data: units } = useJobEquipment(jobId);
  const facts = useJobEquipFacts(units);
  const bank = useInstallBank();
  const [open, setOpen] = useState(false);
  if (!job) return null;
  const prog = specProgress(job.install_spec, facts, bank);
  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className={cn(card, "flex w-full items-center gap-3 text-left active:bg-slate-50")}>
        <span className="min-w-0 flex-1">
          <span className="block text-[16px] font-bold tracking-tight text-slate-900">Installer Guide</span>
          <span className="block text-[13.5px] text-slate-500">
            {facts.lines === 0 ? "Follows the equipment on the job"
              : prog.complete ? `All ${prog.total} answered` : `${prog.answered} of ${prog.total} answered — tap to fill it in`}
          </span>
        </span>
        <span className={cn("rounded-full px-2.5 py-1 text-[12.5px] font-bold", prog.complete ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600")}>
          {prog.complete ? "Done" : "Open"}
        </span>
      </button>
    );
  }
  return <InstallDetailsCard key={job.id} job={job} facts={facts} variant="phone" />;
}
